import React, { Component, Fragment } from 'react';
import pluralize from 'pluralize';
import { debounce, isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import PropTypes from 'prop-types';

import CustomizeLoanAmount from './CustomizeLoanAmount';
import MobileChecker from '../../shared/utils/MobileChecker';
import LoanOfferPreview from './LoanOfferPreview';
import LoanOfferMobilePreview from './LoanOfferMobilePreview';
import { createPaymentSchedules, calculatePaymentSchedules, selectPaymentSchedule } from '../loanOfferActions';
import SchedulePayloadBuilder from '../../../utils/SchedulePayloadBuilder';
import { parseIntUtil } from '../../../utils/NumeralParts';

export class PureDrawDownCustomization extends Component {
  constructor(props) {
    super(props);

    this.state = {
      active_schedule: null,
      credit_line: {},
      credit_limit: {},
      disbursedAmount: 0,
      is_mobile: MobileChecker(),
      isBreakdownVisible: false,
      isLoanAmountChanged: false,
      loading: true,
      loanAmount: 0,
      options: {},
      selectedOption: null,
    };

    this.displayDuration = this.displayDuration.bind(this);
    this.displayFrequency = this.displayFrequency.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.fetchSchedulePreview = debounce(this.fetchSchedulePreview.bind(this), 1000);
  }

  componentDidMount() {
    this.fetchSchedulePreview()
  }

  static propTypes = {
    credit_line: PropTypes.shape({
      client_decision: PropTypes.string,
      convenience_fee_type: PropTypes.string,
      end_timestamp: PropTypes.string,
      id: PropTypes.string,
      start_timestamp: PropTypes.string,
      pre_cutoff_live_date: PropTypes.string,
      post_cutoff_live_date: PropTypes.string,
      option_terms_rate: PropTypes.arrayOf(PropTypes.shape({
        frequency: PropTypes.number.isRequired,
        frequency_mode: PropTypes.string.isRequired,
        term: PropTypes.number.isRequired,
        term_mode: PropTypes.string.isRequired,
        interest_rate: PropTypes.number.isRequired,
        aor: PropTypes.string.isRequired,
      })),
    }),
    credit_limit: PropTypes.shape({
      overdue_count: PropTypes.number,
      expected_unlock_date: PropTypes.string,
      is_credit_limit_locked: PropTypes.bool,
      trigger_exposure: PropTypes.number,
      target_paydown_percent: PropTypes.number,
      credit_remaining: PropTypes.number,
      credit_used: PropTypes.number,
      max_additional_credit_limit: PropTypes.number,
      max_potential_credit_limit: PropTypes.number,
      amount_balance_outstanding: PropTypes.number,
      amount_to_paydown: PropTypes.number,
      amount_to_paydown_with_interest: PropTypes.number,
    }),
  }

  loanPlanSchedule = (frequency, frequency_mode, term_length) => {
    const { option_terms_rate: options } = this.props.credit_line;
    const loanPlanPreviewSchedule = options.find((schedule) => {
      const equalFrequencies = parseIntUtil(schedule['frequency']) === frequency;
      const equalFrequencyModes = schedule['frequency_mode'] === frequency_mode;
      const equalTermLengths = parseIntUtil(schedule['term']) === term_length;
      return equalFrequencies && equalTermLengths && equalFrequencyModes;
    });

    return loanPlanPreviewSchedule;
  };

  getDefaultTermLength() {
    const { option_terms_rate: options } = this.props.credit_line;
    const shortest_term_length = options.sort((schedule1, schedule2) => {
      return parseIntUtil(schedule1.term) - parseIntUtil(schedule2.term);
    })[0];
    return parseIntUtil(shortest_term_length['term']);
  }

  getSortedFrequency(term_length) {
    let schedules = this.getFrequencyByDuration(term_length);
    return schedules.sort((schedule1, schedule2) => {
      const firstMode = schedule1['frequency_mode'];
      const secondMode = schedule2['frequency_mode'];
      const firstFrequency = parseIntUtil(schedule1['frequency']);
      const secondFrequency = parseIntUtil(schedule2['frequency']);
      if (firstMode === secondMode) {
        return secondFrequency - firstFrequency;
      }
      if (firstMode === 'bullet') {
        return 1;
      } else {
        return firstMode > secondMode ? 1 : -1;
      }
    });
  }

  getDefaultFrequency(term_length) {
    const defaultSchedule = this.getSortedFrequency(term_length)[0];

    return {
      frequency_mode: defaultSchedule['frequency_mode'],
      frequency: parseIntUtil(defaultSchedule['frequency']),
      interest_rate: defaultSchedule['interest_rate'],
    };
  }

  get scheduleByDuration() {
    let { option_terms_rate } = this.props.credit_line;
    let schedule_by_duration = {};
    option_terms_rate.forEach((schedule) => {
      let stored_rate =
        schedule_by_duration[schedule['term']] || schedule['aor'];
      if (parseFloat(stored_rate) >= schedule['aor']) {
        schedule_by_duration[schedule['term']] = schedule['aor'];
      }
    });
    return schedule_by_duration;
  }

  get schedule() {
    const { active_schedule } = this.state;
    if (!active_schedule){
      return this.defaultSchedule;
    };
    return active_schedule;
  }

  displayDuration() {
    let schedules_by_term_length = this.scheduleByDuration;
    const { active_schedule } = this.state;
    const schedule = this.schedule;
    return Object.keys(schedules_by_term_length)
      .sort((schedule1, schedule2) => {
        if (schedule1['term_length'] < schedule2['term_length']) return -1;
        return 0;
      })
      .map((term_length) => {
        const aor_rate = parseFloat(
          schedules_by_term_length[term_length]
        ).toFixed(1);
        const term_mode_label = pluralize('month', parseIntUtil(term_length));
        const mainText = `${term_length} ${term_mode_label}`;
        const subText = `from ${aor_rate}%`;
        const testid = `term_termLength_${term_length}`;
        const inactive = parseIntUtil(term_length) !== parseIntUtil(schedule['term_length']);
        const disabled =
          parseIntUtil(term_length) > 3 &&
          active_schedule &&
          active_schedule['frequency_mode'] === 'bullet';
        return this.renderFlexCard(mainText, subText, testid, inactive, disabled);
      });
  }

  getFrequencyByDuration(term_length) {
    const { option_terms_rate } = this.props.credit_line;
    const frequencyOptions = option_terms_rate.filter((schedule) => {
      const invalidBullet = parseIntUtil(term_length) > 3 && schedule['frequency_mode'] === 'bullet'
      return (parseIntUtil(schedule['term']) === parseIntUtil(term_length) && !invalidBullet);
    });
    return frequencyOptions;
  }

  get defaultSchedule() {
    let term_length = this.getDefaultTermLength();
    let default_frequency = this.getDefaultFrequency(term_length);
    return {
      term_length: term_length,
      frequency: default_frequency['frequency'],
      frequency_mode: default_frequency['frequency_mode'],
      interest_rate: default_frequency['interest_rate'],
    };
  }

  displayFrequency() {
    let selectedSchedule = this.schedule;
    const schedules = this.getSortedFrequency(selectedSchedule['term_length']);
    const frequencyCards = schedules.map((schedule) => {
      let main_text = this.getFrequencyLabel(
        schedule['frequency_mode'],
        schedule['frequency']
      );
      const sub_text = `${parseFloat(schedule['aor']).toFixed(1)}%`;
      const test_id = this.buildTempScheduleId(schedule);
      const inactive =
        parseIntUtil(schedule['frequency']) !== selectedSchedule['frequency'] ||
        schedule['frequency_mode'] !== selectedSchedule['frequency_mode'];
      return this.renderFlexCard(
        main_text,
        sub_text,
        test_id,
        inactive
      );
    });
    return frequencyCards;
  }

  getFrequencyLabel(frequency_mode, frequency) {
    if (frequency_mode === 'bullet') return 'Single Repayment'
    if (frequency_mode === 'week' && parseIntUtil(frequency) === 2)
      return 'Every 2 Weeks';
    if (frequency_mode === 'week') return 'Every Week';
    return 'Every Month';
  }

  get selectedLoanPlanSchedule() {
    if (this.state.selectedOption) return this.state.selectedOption;
    const { frequency, frequency_mode, term_length } = this.schedule;
    const loan_plan = this.loanPlanSchedule(
      frequency,
      frequency_mode,
      term_length
    );
    this.setState({ selectedOption: loan_plan });
  }

  renderFlexCardForMobile = (main_text, sub_text, test_id, inactive, disabled) => {
    return (
      <section
        className={`ds-card -primary _boxShadow_none ${
          inactive ? '_disabled' : ''
        } _alignCenter _horizontalMargin--xxsmall _flexCard-mobileOnly`}
        key={test_id}
        data-testid={`${test_id}_container`}
        style={{ flex: '0 0 40.0%', height: '66px' }}
      >
        { disabled ? <div className='display-overlay' /> : null }
        <div
          className='_displayBlock _verticalPadding--none _horizontalPadding--none'
          data-testid={test_id}
          onClick={(e) => {
            this.handleClick(e, test_id);
          }}
          style={{ zIndex: 1 }}
        >
          <p className='subhead _fontSize--normal _paddingTop--small _marginBottom--small-tabletUp _marginBottom--xxsmall-mobileOnly _fontWeight_bold'>
            {main_text}
          </p>
          <p className='_fontSize--normal _paddingBottom--small'>{sub_text}</p>
        </div>
      </section>
    );
  };

  renderFlexCard = (main_text, sub_text, test_id, inactive, disabled=false) => {
    const { is_mobile } = this.state;
    if (is_mobile) {
      return this.renderFlexCardForMobile(
        main_text,
        sub_text,
        test_id,
        inactive,
        disabled
      );
    }
    const className =
      main_text === 'Single Repayment'
        ? `_displayBlock _verticalPadding--none _horizontalPadding--none _width--25p`
        : '_displayBlock _verticalPadding--none _horizontalPadding--none';

    return (
      <section
        className={`ds-card -primary _boxShadow_none ${
          inactive ? '_disabled' : ''
        } _alignCenter _horizontalMargin--small _flexCard`}
        key={test_id}
        data-testid={`${test_id}_container`}
      >
        { disabled ? <div className='display-overlay' /> : null }
        <div
          className={className}
          data-testid={test_id}
          onClick={(e) => {
            if(disabled) return;
            this.handleClick(e, test_id);
          }}
          style={{ zIndex: 1 }}
        >
          <p className='subhead _fontSize--medium _paddingTop--small _marginBottom--small-tabletUp _marginBottom--xxsmall-mobileOnly _fontWeight_bold'>
            {main_text}
          </p>
          <p className='_fontSize--medium _paddingBottom--small'>{sub_text}</p>
        </div>
      </section>
    );
  };

  renderFlexCardContainer(renderDisplay) {
    const { is_mobile } = this.state;
    return (
      <div
        data-testid='duration-options-row'
        className={`_displayFlex _justifyContent-start ${
          is_mobile ? '_overflowX_scroll' : ''
        }`}
        style={{ flexWrap: `${is_mobile ? 'row ' : ''} wrap` }}
      >
        {renderDisplay()}
      </div>
    );
  }

  handleClick(e, test_id) {
    e.preventDefault();

    let optionSelected = test_id.split('_');
    let payload = {};
    if (optionSelected[0] === 'term') {
      const new_term_length = parseIntUtil(optionSelected[2]);
      const default_frequency = this.getDefaultFrequency(new_term_length);
      payload = {
        term_length: new_term_length,
        frequency: default_frequency['frequency'],
        frequency_mode: default_frequency['frequency_mode'],
        interest_rate: default_frequency['interest_rate']
      };
    } else {
      const default_term_length = this.schedule['term_length'];
      const frequencies = this.getSortedFrequency(this.schedule['term_length']);
      let selFrequency = frequencies.filter(
        (frequency) =>
          frequency['frequency_mode'] === optionSelected[2] &&
          parseIntUtil(frequency['frequency']) === parseIntUtil(optionSelected[1])
      )[0];
      payload = {
        term_length: default_term_length,
        frequency: parseIntUtil(optionSelected[1]),
        frequency_mode: optionSelected[2],
        interest_rate: selFrequency['interest_rate']
      };
    }

    this.setState(
      {
        loading: true,
        active_schedule: payload,
      },
      this.fetchSchedulePreview
    );
  }

  loadSelectedOptionForReview = () => {
    const {
      loanOffer: {
        custom_fields
      },
      selectedOption,
    } = this.state;
    const { companyId } = this.props

    this.props.createPaymentSchedules(
      companyId,
      selectedOption,
      (response) => {
        const { disbursed_amount, service_fee_amount } = custom_fields;
        const selected_flex_option_type = 'flexible'
        const service_fee = service_fee_amount === 0 ? 0 : 1.99
        const calculated_service_fee_amount =
          service_fee_amount === 0
            ? disbursed_amount * (1.99 / 100)
            : service_fee_amount;
        const payload = {
          custom_fields: {
            principal_amount: disbursed_amount,
            disbursed_amount,
            service_fee_amount: calculated_service_fee_amount,
            service_fee,
            schedule_id: response.data.data.id,
            repayment_frequency_mode: selectedOption['frequency_mode'],
            selected_flex_option_type,
            term_length: custom_fields.term_length,
          },
          selectedOption
        };
        this.props.selectPaymentSchedule(payload);

        this.props.push({pathname: '/draw_down/review'});
      }
    )
  };

  renderDurations() {
    return this.renderFlexCardContainer(this.displayDuration);
  }

  renderFrequencies() {
    return this.renderFlexCardContainer(this.displayFrequency);
  }

  getLabels(credit_remaining, durations, frequencies) {
    let credit_remaining_label =
      parseFloat(credit_remaining) > 50000
        ? 'Choose a loan amount'
        : 'Your loan amount';
    if (!isEmpty(this.props.fc_payment)) {
      credit_remaining_label = 'Payment Amount';
    }
    const duration_label =
      Object.keys(durations).length > 1
        ? 'Choose a loan duration'
        : 'Your loan duration';
    const frequency_label =
      frequencies.length > 1
        ? 'Choose how often you want to pay back'
        : 'How often you will pay back';

    return {
      credit_remaining_label: credit_remaining_label,
      duration_label: duration_label,
      frequency_label: frequency_label,
    };
  }

  // To get the final loanable amount, we are
  // deducting the total principal amount of active rcl offers.
  getTotalLoanableAmount() {
    const { credit_limit: { credit_remaining, active_rcl_offers_amount_total = 0.0 } } = this.props;
    return credit_remaining - active_rcl_offers_amount_total;
  }

  renderLeftCard() {
    const {
      is_mobile,
    } = this.state;
    const leftColumnWidth = is_mobile ? 100 : 60;
    const loanableAmount = this.getTotalLoanableAmount();
    const {
      credit_remaining_label,
      duration_label,
      frequency_label,
    } = this.getLabels(
      loanableAmount,
      this.scheduleByDuration,
      this.getFrequencyByDuration(this.schedule.term_length)
    );

    return (
      <section
        className='cardSection _padding--normal-mobileOnly _horizontalPadding--xxlarge-tabletUp _marginBottom--medium-tabletUp _borderBottom_unset'
        style={{ width: `${leftColumnWidth}%` }}
      >
        <div className='_marginTop--small _marginBottom--normal'>
          <h2 className='subhead -darkBlue _displayFlex _alignItems-center'>
            <span className='ds-status -secondary _marginRight--small'>1</span>
            <span data-testid='choose-loan-duration-instruction'>
              {credit_remaining_label}
            </span>
          </h2>
          <CustomizeLoanAmount
            loanAmount={loanableAmount}
            onChangeLoanAmount={this.onChangeLoanAmount}
          />
        </div>
        <div className='_marginTop--small _marginBottom--normal'>
          <div className='_displayFlex _alignItems-center'>
            <span className='ds-status -secondary _marginRight--normal'>2</span>
            <span
              className='subhead -darkBlue _lineHeight--normal'
              data-testid='choose-loan-duration-label'
            >
              {duration_label}*
            </span>
          </div>
          {this.renderDurations()}
        </div>
        <div className='_marginTop--small _marginBottom--normal'>
          <div className='_displayFlex _alignItems-center'>
            <span className='ds-status -secondary _marginRight--normal'>3</span>
            <span
              className='subhead -darkBlue _lineHeight--normal'
              data-testid='choose-loan-frequency-label'
            >
              {frequency_label}*
            </span>
          </div>
          {this.renderFrequencies()}
        </div>
        <p className='-secondary _fontSize--small _marginBottom--normal _textAlign_center-mobileOnly'>
          * Interest rates are expressed in AOR per month.
        </p>
        <p className='-secondary _fontSize--small _textAlign_center-mobileOnly'>
          * First Circle will conduct a credit check as part of the approval and disbursement process. Findings from
          these checks may require you to provide further information or may result to changes on your credit line.
        </p>
      </section>
    );
  }

  renderPreviewForDesktop = () => {
    return (
      <div
        className='cardSection -primary _alignSelf-stretch _borderBottomLeftRadius_unset '
        style={{ width: `40%` }}
      >
        <LoanOfferPreview
          loading={this.isRightCardLoading}
          loanOffer={this.state.loanOffer}
          loadingBtn={this.props.loadingCreatePaymentSchedules}
          selectedOption={this.state.selectedOption}
          onClickNext={this.loadSelectedOptionForReview}
          fcPayment={this.props.fc_payment}
        />
      </div>
    );
  };

  renderPreviewForMobile = () => {
    return (
      <LoanOfferMobilePreview
        loading={this.isRightCardLoading}
        loadingBtn={this.props.loadingCreatePaymentSchedules}
        loanOffer={this.state.loanOffer}
        selectedOption={this.state.selectedOption}
        onClickNext={this.loadSelectedOptionForReview}
        toggleBreakdown={this.toggleBreakdown}
        isBreakdownVisible={this.state.isBreakdownVisible}
      />
    );
  };

  renderOverlay = () => {
    const style = {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: '#000',
      opacity: '50%',
      zIndex: 776,
    };
    return <div onClick={this.toggleBreakdown} style={style}></div>;
  };

  get containerStyle() {
    const { isMobile } = this.state;
    let containerBaseHeight = 640;
    if (CustomizeLoanAmount.isCustomizable(this.getTotalLoanableAmount())) {
      containerBaseHeight += 46.0;
    }
    let style = isMobile ? { height: `${containerBaseHeight}px` } : {};
    return style;
  }

  get isRightCardLoading() {
    const { loading } = this.state;
    const { loadingCalculatePaymentSchedules } = this.props;
    return loading || loadingCalculatePaymentSchedules;
  }

  render() {
    const {
      is_mobile: isMobile,
      isBreakdownVisible,
    } = this.state;

    return (
      <Fragment>
        <div
          className='ds-card _withColumns _alignItems-flex-start _displayFlex'
          style={this.containerStyle}
        >
          {this.renderLeftCard()}
          {!isMobile && this.renderPreviewForDesktop()}
        </div>
        {isMobile && this.renderPreviewForMobile()}
        {isBreakdownVisible && this.renderOverlay()}
      </Fragment>
    );
  }

  resetLoading = () => {
    this.setState({ loading: false });
  };

  isProcessingFee(){
    const { convenience_fee_type } = this.props.credit_line;
    return convenience_fee_type === 'Processing Fee'
  }

  buildTempScheduleId(schedule){
    const termLength  = schedule['term_length'] || schedule['term']
    const termMode = schedule['term_mode'] || schedule['termMode'] || 'month';
    return `frequency_${schedule['frequency']}_${schedule['frequency_mode']}_${termLength}_${termMode}`;
  }

  fetchSchedulePreview() {
    const {
      companyId,
      credit_line: {
        convenience_fee_type,
        pre_cutoff_live_date,
        post_cutoff_live_date,
      },
    } = this.props;

    const { loanAmount } = this.state;
    const { frequency, frequency_mode, term_length, interest_rate } = this.schedule;
    const principalAmount = loanAmount === 0 ? this.getTotalLoanableAmount() : loanAmount;
    const optionIds = Object.keys(this.state.options)

    const payload = SchedulePayloadBuilder({
      convenienceFeeType: convenience_fee_type,
      frequency,
      frequencyMode: frequency_mode,
      interestRate: interest_rate,
      preCutoffDate: pre_cutoff_live_date,
      postCutoffDate: post_cutoff_live_date,
      principalAmount,
      termLength: term_length,
      termMode: 'month',
      paymentId: this.props.fc_payment.id,
    });

    const optionId = `${principalAmount}_${this.buildTempScheduleId(
      this.schedule
    )}`;
    if (optionIds.includes(optionId)) {
      this.setPaymentScheduleFromState(optionId, payload);
    } else {
      this.props.calculatePaymentSchedules(
        companyId,
        payload,
        (response) => {
          const selectedOption =
            response.data.data.attributes.plan.loan_plan_preview_schedules[0];
          const service_fee = payload['schedules'][0]['service_fee'];
          const selectedScheduleId = `${principalAmount}_${this.buildTempScheduleId(
            selectedOption
          )}`;
          const service_fee_amount = principalAmount * (service_fee / 100.0);
          const disbursed_amount = principalAmount - service_fee_amount;
          const calculatedOption = {};
          calculatedOption[selectedScheduleId] = selectedOption;

          this.setState({
            selectedOption,
            selectedScheduleId,
            loading: false,
            options: { ...this.state.options, ...calculatedOption },
            loanOffer: {
              custom_fields: {
                disbursed_amount: disbursed_amount,
                loan_amount: principalAmount,
                service_fee_amount,
                service_fee: service_fee,
                term_length,
              },
            },
          });
        },
        (_error) => {
          this.resetLoading();
        }
      );
    }
  }
  setPaymentScheduleFromState(optionId, payload){
    const {options} = this.state
    const {principal_amount, service_fee, term_length} = payload.schedules[0]
    const selectedOption = options[optionId];
    const service_fee_amount = principal_amount * (service_fee / 100.0);
    const disbursed_amount = principal_amount - service_fee_amount;

    this.setState({
      selectedOption,
      selectedScheduleId: optionId,
      loading: false,
      loanOffer: {
        custom_fields: {
          disbursed_amount,
          loan_amount: principal_amount,
          service_fee_amount,
          service_fee,
          term_length,
        },
      },
    });
  }

  formatDate = (date) => {
    let cdate = date
    const year = cdate.getFullYear();
    const month = ('0' + (cdate.getMonth() + 1)).slice(-2);
    const day = cdate.getDate();
    return `${year}-${month}-${day}`;
  }

  onChangeLoanAmount = (value, opts) => {
    if (!opts.changed) {
      this.fetchSchedulePreview.cancel();
    }
    const selectedOption = this.selectedLoanPlanSchedule;
    this.setState(
      {
        isLoanAmountChanged: opts.changed,
        loanAmount: value,
        selectedOption,
      },
      this.handleCustomization
    );
  };

  handleCustomization = () => {
    if (!this.state.isLoanAmountChanged) {
      return this.resetLoading();
    }
    this.setState(
      {
        loading: true,
      },
      this.fetchSchedulePreview
    );
  };

  toggleBreakdown = () => {
    this.setState({
      isBreakdownVisible: !this.state.isBreakdownVisible,
    });
  };
}

const mapStateToProps = (state) => {
  return {
    companyId: state.company.id,
    credit_line: state.creditLine,
    credit_limit: state.creditLimit,
    loadingCalculatePaymentSchedules:
      state.shared.ui.loading.calculatePaymentSchedules,
    loadingCreatePaymentSchedules:
      state.shared.ui.loading.createPaymentSchedules,
    fc_payment: state.fc_payment,
  };
};

const mapDispatchToProps = (dispatch, _props) => {
  return {
    createPaymentSchedules: (leadID, params, onSuccess, onError) =>
      dispatch(createPaymentSchedules(leadID, params, onSuccess, onError)),
    calculatePaymentSchedules: (leadID, params, onSuccess, onError) =>
      dispatch(calculatePaymentSchedules(leadID, params, onSuccess, onError)),
    push: (url) => dispatch(push(url)),
    selectPaymentSchedule: (payload) =>
      dispatch(selectPaymentSchedule(payload)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PureDrawDownCustomization);



// WEBPACK FOOTER //
// ./src/features/loans/components/DrawDownCustomization.js